import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { 
  Box, 
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CenterPointDialog from './LocationEditorTab/CenterPointDialog';
import { gridTheme, gridTokens } from '../../theme/gridTheme';

import { Location, Place, LocationType, ExtendedLocationType } from '../../types/interfaces';
import { flattenLocations } from '../../utils';
import {
  useGetLocationsQuery,
  useGetLocationDetailsQuery,
  useUpdateLocationCenterPointMutation,
  useGetPlacesQuery,
  useDeleteLocationMutation,
} from '../../redux/adminApiSlice';
import AllPlacesTab from './AllPlacesTab/AllPlacesTab';
import LocationEditorTab from './LocationEditorTab/LocationEditorTab';
import NeighborhoodEditor from './NeighborhoodEditor/NeighborhoodEditor';
import GooglePlacesImport from './GooglePlacesImport/GooglePlacesImport';
import LocationModal, { LocationData } from '../../components/LocationModal';
import { SpreadsheetPlain } from '../../spreadsheet/src';
import { ColumnDef, ColumnOrderState, SortingState } from '@tanstack/react-table';
import { useTabFromUrl, useSearch, useSpreadsheetData, useCopyToClipboard, useTableFilters } from '../../hooks';
import AllLocationsTabHeader from './AllLocationsTab/AllLocationsTabHeader';

interface LocationNode extends Location {
  children: (LocationNode | Place)[];
}



// Update the location type guard to be more specific
const isLocation = (item: Location | Place): item is Location => {
  return 'level' in item;
};

const isPlace = (item: Location | Place): item is Place => {
  return 'creator' in item && !('level' in item);
};

// Location `level` in the DB is stored TITLE-CASE (e.g. "Country", "City",
// "Neighborhood") while the LocationType enum is UPPERCASE. Compare
// case-insensitively so hierarchy/validation logic works regardless of the
// stored casing (this was the root cause of every place showing as an orphaned
// error at the tree root).
const levelEq = (
  a: string | ExtendedLocationType | undefined | null,
  b: string | ExtendedLocationType | undefined | null
): boolean => String(a ?? '').toUpperCase() === String(b ?? '').toUpperCase();

// A location's `parent` may be a raw id string or a populated { _id } object.
const getParentIdString = (loc: Location): string | null => {
  const p = loc.parent as string | { _id: string } | undefined | null;
  if (!p) return null;
  return typeof p === 'string' ? p : p._id ?? null;
};

// Component to display post count for a location
const PostCountCell: React.FC<{ postCount: number }> = ({ postCount }) => {
  return <Typography variant="body2">{postCount}</Typography>;
};

const LocationDashboard: React.FC = () => {
  const [locationTree, setLocationTree] = useState<LocationNode[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<Location | Place | null>(null);

  const [showCenterPointDialog, setShowCenterPointDialog] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState<Location | Place | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [selectedLocationForInfo, setSelectedLocationForInfo] = useState<Location | Place | null>(null);
  
  // Column visibility and ordering state
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set());
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);

  // Hooks
  const copyToClipboard = useCopyToClipboard();
  const { tabValue } = useTabFromUrl({
    tabMapping: {
      'editor': 0,
      'all': 1,
      'places': 2,
      'neighborhood-editor': 3,
      'google-places-import': 4,
    },
    defaultTab: 0,
  });

  // RTK Query hooks
  const { data: locations = [], isLoading: isLoadingLocations, refetch: refetchLocations } = useGetLocationsQuery();
  const { data: places = [], isLoading: isLoadingPlaces, refetch: refetchPlaces } = useGetPlacesQuery();
  const [updateLocationCenterPoint] = useUpdateLocationCenterPointMutation();
  const [deleteLocation] = useDeleteLocationMutation();

  // Search hook for filtering locations/places
  const allItems = [...locations, ...places];
  const { searchQuery, setSearchQuery, filteredData: filteredItems } = useSearch<Location | Place>({
    data: allItems,
    searchFn: (item, query) => {
      return item._id.toLowerCase().includes(query) ||
             item.name.toLowerCase().includes(query);
    },
  });

  // Column filters
  const { columnFilters, setColumnFilters } = useTableFilters();
  
  // Wrapper to log filter changes
  const handleColumnFiltersChange = useCallback((filters: Record<string, string[]>) => {
    console.log('[LocationsPage] handleColumnFiltersChange called with:', filters);
    setColumnFilters(filters);
  }, [setColumnFilters]);
  
  // Debug: log when columnFilters change
  useEffect(() => {
    const hasFilters = Object.keys(columnFilters).length > 0 && 
      Object.values(columnFilters).some(f => f.length > 0);
    if (hasFilters) {
      console.log('[LocationsPage] columnFilters state changed:', columnFilters);
    }
  }, [columnFilters]);

  // Memoize filter function to prevent unnecessary re-renders
  const filterFn = useCallback((item: Location | Place, filters: Record<string, string[]>) => {
    // Filter by level - normalize to uppercase for comparison
    if (filters.level && filters.level.length > 0) {
      const itemLevel = isLocation(item) ? String(item.level).toUpperCase() : 'PLACE';
      const normalizedFilters = filters.level.map(f => String(f).toUpperCase());
      if (!normalizedFilters.includes(itemLevel)) {
        return false;
      }
    }

    // Filter by name - case-insensitive comparison
    if (filters.name && filters.name.length > 0) {
      const itemName = (item.name || '').toLowerCase();
      const normalizedNameFilters = filters.name.map(f => String(f).toLowerCase());
      if (!normalizedNameFilters.includes(itemName)) {
        return false;
      }
    }

    // Filter by photo existence
    if (filters.hasPhoto && filters.hasPhoto.length > 0) {
      const photo = isLocation(item) ? item.photo : (isPlace(item) ? item.photo : null);
      const hasPhoto = photo && photo.trim() !== '';
      const hasPhotoValue = hasPhoto ? 'Yes' : 'No';
      if (!filters.hasPhoto.includes(hasPhotoValue)) {
        return false;
      }
    }

    return true;
  }, []);

  // Transform data to include id field and apply column filters
  const { transformedData: itemsWithId, filteredData: filteredItemsWithId } = useSpreadsheetData<Location | Place>({
    data: filteredItems,
    columnFilters,
    filterFn,
  });
  
  // Debug: log filtered data counts when filters change
  useEffect(() => {
    const hasFilters = Object.keys(columnFilters).length > 0 && 
      Object.values(columnFilters).some(f => f.length > 0);
    if (hasFilters) {
      console.log('[LocationsPage] Data counts with filters:', {
        original: filteredItems.length,
        transformed: itemsWithId.length,
        filtered: filteredItemsWithId.length,
        columnFilters,
      });
    }
  }, [filteredItems.length, itemsWithId.length, filteredItemsWithId.length, columnFilters]);


  // Compute unique values for filtering
  const uniqueValues = useMemo(() => {
    const values: Record<string, string[]> = {
      level: [],
      name: [],
      hasPhoto: ['Yes', 'No'], // Photo column filter values
    };

    itemsWithId.forEach(item => {
      // Level values
      if (isLocation(item)) {
        const level = item.level || 'PLACE';
        if (!values.level.includes(level)) {
          values.level.push(level);
        }
      } else {
        if (!values.level.includes('PLACE')) {
          values.level.push('PLACE');
        }
      }

      // Name values (for reference, but probably too many to be useful)
      if (item.name && !values.name.includes(item.name)) {
        values.name.push(item.name);
      }
    });

    // Sort values
    values.level.sort();
    values.name.sort();

    return values;
  }, [itemsWithId]);

  const handleOpenInfo = useCallback((location: Location | Place) => {
    setSelectedLocationForInfo(location);
  }, []);

  // Build locationData from the selected location/place for LocationModal
  const locationDataForModal = useMemo((): LocationData | null => {
    if (!selectedLocationForInfo) return null;

    const locationData: LocationData = {};

    if (isPlace(selectedLocationForInfo)) {
      // For places, set the place and its parent locations
      locationData.place = selectedLocationForInfo;
      if (selectedLocationForInfo.neighborhood) {
        locationData.neighborhood = typeof selectedLocationForInfo.neighborhood === 'object' 
          ? selectedLocationForInfo.neighborhood 
          : locations.find(l => l._id === selectedLocationForInfo.neighborhood) || null;
      }
      if (selectedLocationForInfo.city) {
        locationData.city = typeof selectedLocationForInfo.city === 'object'
          ? selectedLocationForInfo.city
          : locations.find(l => l._id === selectedLocationForInfo.city) || null;
      }
      if (selectedLocationForInfo.state) {
        locationData.state = typeof selectedLocationForInfo.state === 'object'
          ? selectedLocationForInfo.state
          : locations.find(l => l._id === selectedLocationForInfo.state) || null;
      }
      if (selectedLocationForInfo.country) {
        locationData.country = typeof selectedLocationForInfo.country === 'object'
          ? selectedLocationForInfo.country
          : locations.find(l => l._id === selectedLocationForInfo.country) || null;
      }
    } else if (isLocation(selectedLocationForInfo)) {
      // For locations, set the location at its level and find parent locations
      const level = selectedLocationForInfo.level?.toLowerCase();
      if (level === 'neighborhood') {
        locationData.neighborhood = selectedLocationForInfo;
        // Find parent city
        if (selectedLocationForInfo.parent) {
          const parentCity = locations.find(l => l._id === selectedLocationForInfo.parent);
          if (parentCity) {
            locationData.city = parentCity;
            // Find parent state
            if (parentCity.parent) {
              const parentState = locations.find(l => l._id === parentCity.parent);
              if (parentState) {
                locationData.state = parentState;
                // Find parent country
                if (parentState.parent) {
                  const parentCountry = locations.find(l => l._id === parentState.parent);
                  if (parentCountry) {
                    locationData.country = parentCountry;
                  }
                }
              }
            }
          }
        }
      } else if (level === 'city') {
        locationData.city = selectedLocationForInfo;
        // Find parent state
        if (selectedLocationForInfo.parent) {
          const parentState = locations.find(l => l._id === selectedLocationForInfo.parent);
          if (parentState) {
            locationData.state = parentState;
            // Find parent country
            if (parentState.parent) {
              const parentCountry = locations.find(l => l._id === parentState.parent);
              if (parentCountry) {
                locationData.country = parentCountry;
              }
            }
          }
        }
      } else if (level === 'state') {
        locationData.state = selectedLocationForInfo;
        // Find parent country
        if (selectedLocationForInfo.parent) {
          const parentCountry = locations.find(l => l._id === selectedLocationForInfo.parent);
          if (parentCountry) {
            locationData.country = parentCountry;
          }
        }
      } else if (level === 'country') {
        locationData.country = selectedLocationForInfo;
      }
    }

    return locationData;
  }, [selectedLocationForInfo, locations]);


  const handleConfirmDelete = useCallback(async () => {
    if (!locationToDelete) return;
    
    try {
      setDeleteError(null);
      console.log('[LocationsPage] Deleting location:', locationToDelete._id);
      const level = ('level' in locationToDelete) ? locationToDelete.level : 'Place';
      await deleteLocation({ 
        id: locationToDelete._id,
        level
      }).unwrap();
      
      console.log('[LocationsPage] Location deleted successfully');
      setDeleteSuccess(true);
      setDeleteConfirmDialog(false);
      
      // Refetch locations to update the list
      await refetchLocations();
      await refetchPlaces();
      
      // Clear success message after 3 seconds
      setTimeout(() => setDeleteSuccess(false), 3000);
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.message || 'Failed to delete location';
      console.error('[LocationsPage] Failed to delete location:', error);
      setDeleteError(errorMessage);
    }
  }, [locationToDelete, deleteLocation, refetchLocations, refetchPlaces]);

  // Update the buildLocationTree function
  const buildLocationTree = (locations: Location[], places: any[]): LocationNode[] => {
    const locationMap = new Map<string, LocationNode>();
    const rootNodes: LocationNode[] = [];

    // Filter out places from locations array and only sort the regular locations
    const regularLocations = locations.filter(isLocation);

    const levelOrder = [
      LocationType.GLOBAL,
      LocationType.REGION,
      LocationType.COUNTRY,
      LocationType.STATE,
      LocationType.CITY,
      LocationType.NEIGHBORHOOD
    ] as const;

    const levelRank = (lvl: string): number =>
      levelOrder.findIndex((l) => levelEq(l, lvl));

    const sortedLocations = [...regularLocations].sort((a, b) => {
      return levelRank(a.level) - levelRank(b.level);
    });

    // First pass: Create location nodes
    sortedLocations.forEach(location => {
      locationMap.set(location._id, { ...location, children: [] });
    });

    // Cities that actually contain at least one neighborhood. A place in a city
    // that has NO neighborhoods legitimately has no neighborhood of its own — it
    // should nest under the city, not be flagged as an orphan.
    const citiesWithNeighborhoods = new Set<string>();
    sortedLocations.forEach(loc => {
      if (levelEq(loc.level, LocationType.NEIGHBORHOOD) && loc.parent) {
        const cityId = typeof loc.parent === 'string' ? loc.parent : loc.parent._id;
        if (cityId) citiesWithNeighborhoods.add(String(cityId));
      }
    });

    // Second pass: Build location hierarchy
    sortedLocations.forEach(location => {
      const node = locationMap.get(location._id)!;
      if (location.parent) {
        const parentId = typeof location.parent === 'string' ? location.parent : location.parent._id;
        const parentNode = locationMap.get(parentId);
        if (parentNode && isLocation(parentNode)) {
          // (Parent/child relationship validation is surfaced later via
          // getLocationIssues; nesting itself is casing-insensitive so it works
          // whether level is stored title-case or uppercase.)
          parentNode.children = parentNode.children || [];
          parentNode.children.push(node);
          parentNode.children.sort((a, b) => a.name.localeCompare(b.name));
        } else {
          rootNodes.push(node);
        }
      } else {
        // Only GLOBAL and COUNTRY should be root nodes
        if (location.level !== LocationType.GLOBAL && location.level !== LocationType.COUNTRY) {
          // Silently collect issues instead of logging each one
        }
        rootNodes.push(node);
      }
    });

    // Add places to their parent neighborhoods
    places.forEach(place => {
      const placeNode = {
        ...place,
        children: [] as (Location | Place)[],
        level: 'PLACE' as ExtendedLocationType
      };

      // `neighborhood`/`city` may be a raw ObjectId string OR a populated object
      // ({ _id, name }) depending on the endpoint. Normalize to an ID string so
      // it matches locationMap (keyed by _id).
      const neighborhoodId =
        typeof place.neighborhood === 'object' && place.neighborhood !== null
          ? place.neighborhood._id
          : place.neighborhood;
      const cityId =
        typeof place.city === 'object' && place.city !== null ? place.city._id : place.city;

      const attachTo = (parentId: string | undefined | null): boolean => {
        if (!parentId) return false;
        const parentNode = locationMap.get(String(parentId));
        if (parentNode && isLocation(parentNode)) {
          parentNode.children = parentNode.children || [];
          parentNode.children.push(placeNode);
          parentNode.children.sort((a, b) => a.name.localeCompare(b.name));
          return true;
        }
        return false;
      };

      // 1) Prefer the assigned neighborhood.
      if (neighborhoodId && attachTo(neighborhoodId)) return;

      // 2) No (valid) neighborhood: if the city has no neighborhoods at all,
      //    nesting the place directly under the city is correct (not an error).
      const cityHasNoNeighborhoods = cityId && !citiesWithNeighborhoods.has(String(cityId));
      if (cityHasNoNeighborhoods && attachTo(cityId)) return;

      // 3) Genuinely orphaned (bad neighborhood id, or city missing/has nbhds
      //    but place lacks one) — surface at the root so it's visible/fixable.
      rootNodes.push(placeNode as unknown as LocationNode);
    });

    // Sort root nodes
    rootNodes.sort((a, b) => a.name.localeCompare(b.name));
    return rootNodes;
  };




  // Define columns for SpreadsheetPlain
  const columns: ColumnDef<(Location | Place) & { id: string }>[] = useMemo(() => [
    {
      accessorKey: '_id',
      header: 'ID',
      size: 100,
      minSize: 90,
      maxSize: 110,
      enableSorting: true,
      cell: ({ getValue, row }) => {
        const id = getValue() as string;
        const item = row.original;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
            <Tooltip title="View Details">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenInfo(item);
                }}
                sx={{ color: 'primary.main' }}
              >
                <InfoIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={`Copy ID: ${id}`}>
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  copyToClipboard(id);
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      accessorKey: 'name',
      header: 'Name',
      size: 200,
      minSize: 150,
      maxSize: 300,
      enableSorting: true,
      cell: ({ getValue }) => {
        const name = getValue() as string;
        return <Typography variant="body2">{name}</Typography>;
      },
    },
    {
      accessorKey: 'level',
      header: 'Level',
      size: 150,
      minSize: 120,
      maxSize: 180,
      enableSorting: true,
      cell: ({ row }) => {
        const item = row.original;
        const level = isLocation(item) ? item.level : 'PLACE';
        return <Typography variant="body2">{level}</Typography>;
      },
    },
    {
      id: 'children',
      header: 'Children',
      size: 100,
      minSize: 80,
      maxSize: 120,
      enableSorting: true,
      cell: ({ row }) => {
        const item = row.original;
        const count = isLocation(item) ? (item.children || []).length : 0;
        return <Typography variant="body2">{count}</Typography>;
      },
    },
    {
      id: 'totalPosts',
      header: 'Total Posts',
      size: 120,
      minSize: 100,
      maxSize: 150,
      enableSorting: true,
      accessorFn: (row) => {
        // Use totalPosts from Location model, or 0 for Places
        // Ensure we return a number for proper numeric sorting
        if (isLocation(row)) {
          const count = row.totalPosts;
          return typeof count === 'number' ? count : 0;
        }
        return 0; // Places don't have totalPosts field
      },
      sortingFn: (rowA, rowB, columnId) => {
        // Custom numeric sorting function
        const a = rowA.getValue(columnId) as number;
        const b = rowB.getValue(columnId) as number;
        return a - b;
      },
      cell: ({ row }) => {
        const item = row.original;
        const postCount = isLocation(item) ? (item.totalPosts ?? 0) : 0;
        return <PostCountCell postCount={postCount} />;
      },
    },
    {
      id: 'onlineUsers',
      header: 'Online Users',
      size: 120,
      minSize: 100,
      maxSize: 150,
      enableSorting: true,
      accessorFn: (row) => {
        // Use onlineUsers from Location model, or 0 for Places
        if (isLocation(row)) {
          const count = row.onlineUsers;
          return typeof count === 'number' ? count : 0;
        }
        return 0; // Places don't have onlineUsers field
      },
      sortingFn: (rowA, rowB, columnId) => {
        // Custom numeric sorting function
        const a = rowA.getValue(columnId) as number;
        const b = rowB.getValue(columnId) as number;
        return a - b;
      },
      cell: ({ row }) => {
        const item = row.original;
        const onlineCount = isLocation(item) ? (item.onlineUsers ?? 0) : 0;
        return <Typography variant="body2">{onlineCount}</Typography>;
      },
    },
    {
      id: 'hasPhoto',
      header: 'Photo',
      size: 60,
      minSize: 50,
      maxSize: 70,
      enableSorting: true,
      accessorFn: (row) => {
        // Return 1 if has photo, 0 if not (for sorting)
        const photo = isLocation(row) ? row.photo : (isPlace(row) ? row.photo : null);
        return photo && photo.trim() !== '' ? 1 : 0;
      },
      sortingFn: (rowA, rowB, columnId) => {
        const a = rowA.getValue(columnId) as number;
        const b = rowB.getValue(columnId) as number;
        return a - b;
      },
      cell: ({ row }) => {
        const item = row.original;
        const photo = isLocation(item) ? item.photo : (isPlace(item) ? item.photo : null);
        const hasPhoto = photo && photo.trim() !== '';
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {hasPhoto ? (
              <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
            ) : (
              <CancelIcon sx={{ fontSize: 18, color: 'error.main' }} />
            )}
          </Box>
        );
      },
    },
    {
      id: 'coordinates',
      header: 'Coordinates',
      size: 200,
      minSize: 150,
      maxSize: 250,
      enableSorting: false,
      cell: ({ row }) => {
        const item = row.original;
        if (isLocation(item) && item.coordinates?.coordinates) {
          const coords = item.coordinates.coordinates;
          return <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
            [{coords[0].toFixed(2)}, {coords[1].toFixed(2)}]
          </Typography>;
        }
        if (isPlace(item)) {
          const place = item as Place;
          if (place.location?.coordinates) {
            const coords = place.location.coordinates;
            return <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
              [{coords[0].toFixed(2)}, {coords[1].toFixed(2)}]
            </Typography>;
          }
        }
        return <Typography variant="body2">—</Typography>;
      },
    },
  ], [copyToClipboard, handleOpenInfo]);

  // Initialize visible columns with all columns visible
  useEffect(() => {
    const columnIds = columns.map(col => {
      if (col.id) return col.id;
      if ('accessorKey' in col && col.accessorKey) return String(col.accessorKey);
      return null;
    }).filter((id): id is string => Boolean(id));
    if (columnIds.length > 0 && visibleColumns.size === 0) {
      setVisibleColumns(new Set(columnIds));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]);

  // Handle column visibility change
  const handleColumnVisibilityChange = useCallback((columnId: string, visible: boolean) => {
    setVisibleColumns(prev => {
      const next = new Set(prev);
      if (visible) {
        next.add(columnId);
      } else {
        next.delete(columnId);
      }
      return next;
    });
  }, []);

  // Filter columns based on visibility
  const visibleColumnsArray = useMemo(() => {
    return columns.filter(col => {
      let columnId: string | undefined;
      if (col.id) {
        columnId = col.id;
      } else if ('accessorKey' in col && col.accessorKey) {
        columnId = String(col.accessorKey);
      }
      return columnId && visibleColumns.has(columnId);
    });
  }, [columns, visibleColumns]);

  // Skip location details query if no location is selected
  const { refetch: refetchLocationDetails } = useGetLocationDetailsQuery(
    selectedLocation?._id ?? 'skip',
    { skip: !selectedLocation?._id }
  );

  const handleSubmit = (lat: number, lng: number) => {
    if (selectedLocation) {
      updateLocationCenterPoint({
        id: selectedLocation._id,
        coordinates: [lng, lat]
      });
      setShowCenterPointDialog(false);
    }
  };

  const handleCloseInfo = () => {
    setSelectedLocationForInfo(null);
  };


  // Add handleLocationUpdated function
  const handleLocationUpdated = async () => {
    if (selectedLocation?._id) {
      refetchLocationDetails();
    }
    await refetchLocations();
    await refetchPlaces();
  };

  // Update the getLocationIssues function to handle type assertions
  const getLocationIssues = (node: Location | Place, tree: (Location | Place)[]): string[] => {
    const issues: string[] = [];
    
    if (isLocation(node)) {
      // Location-specific checks
      if (!levelEq(node.level, LocationType.COUNTRY) && !node.coordinates?.coordinates) {
        issues.push('Missing center point');
      }
      
      if (!levelEq(node.level, LocationType.COUNTRY) && !node.boundingBox?.coordinates) {
        issues.push('Invalid or missing bounding box');
      }

      if (levelEq(node.level, LocationType.CITY) && node.parent) {
        const parentNode = tree.flatMap(n => {
          const flattenLocations = (n: Location | Place): (Location | Place)[] => {
            if (!isLocation(n)) return [n];
            const nodeAsLocation = n as Location & { children: (Location | Place)[] };
            return [n, ...nodeAsLocation.children.flatMap(flattenLocations)];
          };
          return flattenLocations(n);
        }).find(n => isLocation(n) && n._id === node.parent);

        if (parentNode && isLocation(parentNode) && levelEq(parentNode.level, LocationType.CITY)) {
          issues.push(`Invalid hierarchy: Cannot be a child of ${parentNode.name} (City)`);
        }
      }
    } else {
      // Place-specific checks
      if (!node.location?.coordinates) {
        issues.push('Missing coordinates');
      }
      // Normalize neighborhood (may be a populated object or a raw ID string).
      const nodeNeighborhoodId =
        typeof node.neighborhood === 'object' && node.neighborhood !== null
          ? (node.neighborhood as { _id: string })._id
          : node.neighborhood;

      // Flatten all locations in the tree once for lookups below.
      const allLocations = tree.flatMap(n => {
        const flattenLocations = (n: Location | Place): (Location | Place)[] => {
          if (!isLocation(n)) return [n];
          const nodeAsLocation = n as Location & { children: (Location | Place)[] };
          return [n, ...nodeAsLocation.children.flatMap(flattenLocations)];
        };
        return flattenLocations(n);
      });

      if (!nodeNeighborhoodId) {
        // A place legitimately has no neighborhood when its city has none. Only
        // flag it if the city actually contains neighborhoods (or we can't tell).
        const placeCityId =
          typeof node.city === 'object' && node.city !== null
            ? (node.city as { _id: string })._id
            : node.city;

        const cityHasNeighborhoods =
          !!placeCityId &&
          allLocations.some(
            n =>
              isLocation(n) &&
              levelEq(n.level, LocationType.NEIGHBORHOOD) &&
              getParentIdString(n) === String(placeCityId)
          );

        if (cityHasNeighborhoods) {
          issues.push('Place has no neighborhood assigned');
        }
      } else {
        const neighborhoodExists = allLocations.some(
          n => isLocation(n) && n._id === nodeNeighborhoodId && levelEq(n.level, LocationType.NEIGHBORHOOD)
        );

        if (!neighborhoodExists) {
          issues.push('Assigned neighborhood not found in location tree');
        }
      }
    }
    
    return issues;
  };

  // Update the handleLocationSelect function
  const handleLocationSelect = async (nodeId: string) => {
    const location = locationTree.flatMap(node => flattenLocations(node))
      .find(loc => loc._id === nodeId);

    if (location) {
      setSelectedLocation(location);
      // Note: No need to manually refetch - RTK Query will automatically 
      // fetch location details when selectedLocation changes due to the query dependency
    }
  };



  // Update location tree when RTK Query data changes
  useEffect(() => {
    if (locations.length > 0) {
      const tree = buildLocationTree(locations as Location[], places);
      if (tree && tree.length > 0) {
        // Only update if the tree has actually changed
        setLocationTree(prevTree => {
          if (prevTree.length !== tree.length) {
            return tree;
          }
          // Check if the tree content has changed by comparing root node IDs
          const prevIds = prevTree.map(node => node._id).sort();
          const newIds = tree.map(node => node._id).sort();
          const hasChanged = JSON.stringify(prevIds) !== JSON.stringify(newIds);
          
          if (hasChanged) {
            return tree;
          } else {
            return prevTree;
          }
        });
      }
    }
  }, [locations, places]);















  // Update renderPlacesContent function to return JSX
  const renderPlacesContent = () => {
    return <AllPlacesTab />;
  };

  // Update renderLocationEditorContent function
  const renderLocationEditorContent = () => (
    <LocationEditorTab
      locationTree={locationTree}
      selectedLocation={selectedLocation}
      onLocationSelect={handleLocationSelect}
      getLocationIssues={getLocationIssues}
      onLocationUpdated={handleLocationUpdated}
    />
  );

  // Render the all locations content
  const renderAllLocationsContent = () => (
    <Box sx={{ 
      width: '100%', 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      <AllLocationsTabHeader
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchResultsCount={filteredItems.length}
        columns={columns}
        visibleColumns={visibleColumns}
        onColumnVisibilityChange={handleColumnVisibilityChange}
      />

      {deleteSuccess && (
        <Alert severity="success" sx={{ mb: 2, flexShrink: 0, mx: 2 }}>
          Location deleted successfully!
        </Alert>
      )}

      <Box sx={{ 
        flex: 1, 
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        <SpreadsheetPlain
          darkMode
          data={filteredItemsWithId}
          columns={visibleColumnsArray}
          enableFiltering={true}
          filterableColumns={['level', 'name', 'hasPhoto']}
          uniqueValues={uniqueValues}
          columnFilters={columnFilters}
          onColumnFiltersChange={handleColumnFiltersChange}
          enableColumnReordering={true}
          columnOrder={columnOrder}
          onColumnOrderChange={setColumnOrder}
          sorting={sorting}
          onSortingChange={setSorting}
        />
      </Box>

      {selectedLocationForInfo && locationDataForModal && (
        <LocationModal
          isOpen={!!selectedLocationForInfo}
          onClose={handleCloseInfo}
          locationData={locationDataForModal}
          currentPostLevel={isLocation(selectedLocationForInfo) ? selectedLocationForInfo.level : undefined}
          onLocationClick={(clickedLocation, locationLevel) => {
            // When a location is clicked in the modal, update the selected location
            setSelectedLocationForInfo(clickedLocation);
          }}
        />
      )}

      <CenterPointDialog
        open={showCenterPointDialog}
        onClose={() => setShowCenterPointDialog(false)}
        handleSubmit={handleSubmit}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog}
        onClose={() => {
          setDeleteConfirmDialog(false);
          setLocationToDelete(null);
          setDeleteError(null);
        }}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <DialogContentText>
            Are you sure you want to delete <strong>{locationToDelete?.name}</strong>?
            {locationToDelete && ('level' in locationToDelete) && (
              <Box sx={{ mt: 1 }}>
                <strong>Level:</strong> {locationToDelete.level}
              </Box>
            )}
            {locationToDelete && !('level' in locationToDelete) && (
              <Box sx={{ mt: 1 }}>
                <strong>Type:</strong> Place
              </Box>
            )}
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteConfirmDialog(false);
              setLocationToDelete(null);
              setDeleteError(null);
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  if (isLoadingLocations || isLoadingPlaces) {
    return (
      <ThemeProvider theme={gridTheme}>
        <Box p={3} sx={{ bgcolor: gridTokens.bgPrimary, color: gridTokens.textPrimary, height: '100%' }}>
          <Typography>Loading...</Typography>
        </Box>
      </ThemeProvider>
    );
  }



  return (
    <ThemeProvider theme={gridTheme}>
      <Box sx={{ 
        width: '100%', 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: gridTokens.bgPrimary,
        color: gridTokens.textPrimary,
      }}>
        {/* Render content based on selected tab */}
        {tabValue === 0 && renderLocationEditorContent()}
        {tabValue === 1 && renderAllLocationsContent()}
        {tabValue === 2 && renderPlacesContent()}
        {tabValue === 3 && <NeighborhoodEditor />}
        {tabValue === 4 && <GooglePlacesImport />}
      </Box>
    </ThemeProvider>
  );
};

export default LocationDashboard; 